"use client";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Info, Upload } from "lucide-react";
import { useRef } from "react";
import { Button } from "@/components/ui/button";

interface JWTFormProps {
  config: Record<string, unknown>;
  onChange: (config: Record<string, unknown>) => void;
  connectionConfig: Record<string, unknown>;
  onConfigChange: (config: Record<string, unknown>) => void;
}

const GOOGLE_SCOPES = [
  { value: "https://www.googleapis.com/auth/gmail.readonly", label: "Gmail (Read)" },
  { value: "https://www.googleapis.com/auth/gmail.send", label: "Gmail (Send)" },
  { value: "https://www.googleapis.com/auth/drive", label: "Google Drive" },
  { value: "https://www.googleapis.com/auth/drive.readonly", label: "Google Drive (Read)" },
  { value: "https://www.googleapis.com/auth/calendar", label: "Google Calendar" },
  { value: "https://www.googleapis.com/auth/calendar.readonly", label: "Google Calendar (Read)" },
  { value: "https://www.googleapis.com/auth/spreadsheets", label: "Google Sheets" },
  { value: "https://www.googleapis.com/auth/spreadsheets.readonly", label: "Google Sheets (Read)" },
  { value: "https://www.googleapis.com/auth/cloud-platform", label: "Google Cloud Platform" },
];

export function JWTForm({
  config,
  onChange,
  connectionConfig,
  onConfigChange,
}: JWTFormProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const json = JSON.parse(event.target?.result as string);
        
        // Extract service account details from Google JSON key file
        if (json.type === "service_account") {
          onChange({
            service_account_email: json.client_email,
            private_key: json.private_key,
            project_id: json.project_id,
          });
        } else {
          onChange({ private_key: event.target?.result as string });
        }
      } catch {
        // If not JSON, treat as raw private key
        onChange({ private_key: event.target?.result as string });
      }
    };
    reader.readAsText(file);
  };

  const selectedScopes = ((connectionConfig.scopes as string[]) || []);

  const toggleScope = (scope: string) => {
    const newScopes = selectedScopes.includes(scope)
      ? selectedScopes.filter((s: string) => s !== scope)
      : [...selectedScopes, scope];
    onConfigChange({ scopes: newScopes });
  };

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-blue-500/20 bg-blue-500/10 p-3">
        <div className="flex items-start gap-2">
          <Info className="mt-0.5 h-4 w-4 text-blue-400" />
          <div className="text-xs text-blue-200">
            <p className="font-medium">Google Service Account / JWT Authentication</p>
            <p className="mt-1 text-blue-200/70">
              Upload your service account JSON key file or enter credentials manually.
            </p>
          </div>
        </div>
      </div>

      {/* File Upload */}
      <div className="space-y-2">
        <Label className="text-white/80">Service Account Key File</Label>
        <input
          ref={fileInputRef}
          type="file"
          accept=".json"
          onChange={handleFileUpload}
          className="hidden"
        />
        <Button
          type="button"
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          className="w-full border-dashed border-white/20 bg-white/5 text-white/70 hover:bg-white/10"
        >
          <Upload className="mr-2 h-4 w-4" />
          Upload JSON Key File
        </Button>
        <p className="text-xs text-white/40">
          Upload the JSON key file downloaded from Google Cloud Console
        </p>
      </div>

      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-white/10" />
        </div>
        <div className="relative flex justify-center text-xs">
          <span className="bg-slate-900 px-2 text-white/40">or enter manually</span>
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-white/80">Service Account Email</Label>
        <Input
          value={(config.service_account_email as string) || ""}
          onChange={(e) => onChange({ service_account_email: e.target.value })}
          placeholder="service-account@project.iam.gserviceaccount.com"
          className="border-white/10 bg-white/5 font-mono text-sm text-white placeholder:text-white/40"
        />
      </div>

      <div className="space-y-2">
        <Label className="text-white/80">Private Key</Label>
        <Textarea
          value={(config.private_key as string) || ""}
          onChange={(e) => onChange({ private_key: e.target.value })}
          placeholder="-----BEGIN PRIVATE KEY-----\n...\n-----END PRIVATE KEY-----"
          className="min-h-[120px] border-white/10 bg-white/5 font-mono text-xs text-white placeholder:text-white/40"
        />
      </div>

      <div className="space-y-2">
        <Label className="text-white/80">Delegated User Email (Optional)</Label>
        <Input
          value={(config.delegated_user_email as string) || ""}
          onChange={(e) => onChange({ delegated_user_email: e.target.value })}
          placeholder="user@yourdomain.com"
          className="border-white/10 bg-white/5 text-white placeholder:text-white/40"
        />
        <p className="text-xs text-white/40">
          For domain-wide delegation to access user data
        </p>
      </div>

      {/* Scopes */}
      <div className="space-y-2">
        <Label className="text-white/80">API Scopes</Label>
        <div className="flex flex-wrap gap-2">
          {GOOGLE_SCOPES.map((scope) => (
            <button
              key={scope.value}
              type="button"
              onClick={() => toggleScope(scope.value)}
              className={`rounded-md border px-2 py-1 text-xs transition-colors ${
                selectedScopes.includes(scope.value)
                  ? "border-emerald-500/50 bg-emerald-500/20 text-emerald-400"
                  : "border-white/10 bg-white/5 text-white/60 hover:bg-white/10"
              }`}
            >
              {scope.label}
            </button>
          ))}
        </div>
        <p className="text-xs text-white/40">
          Select the scopes your service account needs
        </p>
      </div>

      {selectedScopes.length > 0 && (
        <div className="rounded-lg border border-white/10 bg-white/5 p-3">
          <p className="text-xs text-white/60">Selected scopes:</p>
          <p className="mt-1 font-mono text-xs text-white/80 break-all">
            {selectedScopes.join(" ")}
          </p>
        </div>
      )}
    </div>
  );
}
